// apps/console/src/hooks/useRobotAlertsPolling.ts
//
// Reconciles the robot-alerts store against the database every 5s.
//
// The live channel remains the fast path — a pushed alert lands in well under a second and this
// poll will not beat it. What the poll fixes is the case where the push never arrives at all: a
// websocket that dropped and is still backing off, a tab that was asleep through the delivery, or a
// snapshot that was taken a moment before the alert was written. In each of those the inbox looks
// calm and is simply wrong, which is the one failure mode an alert list must not have.
//
// One interval is shared by every consumer (bell, inbox, dashboard element) via a refcount: three
// components mounting must not mean three requests every five seconds.

import { useEffect } from 'react'

import { robotsApi } from '../services/api/domains/robots.api'
import { reconcileRobotAlerts, type RobotAlertItem } from '../services/api/robot-alerts-store'
import { getLiveChannelUrl } from '../services/auth/config'

export const ROBOT_ALERTS_POLL_MS = 5_000

let refCount = 0
let timer: ReturnType<typeof setInterval> | null = null
/** Guards against a slow response overlapping the next tick and reordering the two writes. */
let inFlight = false

/**
 * True when this deployment actually has a robot channel.
 *
 * The same gate `RobotAlertsBell` and `AppLayout` already apply. Without it the poll runs on every
 * authenticated page for every tenant — including on-prem with no broker, and tenants with no
 * robots at all — costing a Lambda invocation every 5 seconds per open tab to reconcile a feature
 * that is not present. Measured against the dev server's 60 req/min/IP limiter, the poll plus its
 * CORS preflight alone accounted for roughly a third of the budget and produced real 429s.
 */
function hasLiveChannel(): boolean {
  try {
    return getLiveChannelUrl() !== null
  } catch {
    return false
  }
}

/** A hidden tab is not being read. Nothing is gained by reconciling a list nobody is looking at. */
function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

async function pollOnce(): Promise<void> {
  if (inFlight || !isVisible()) return
  inFlight = true

  // Captured BEFORE the request so the store can tell what this response could not have known
  // about — anything that happened after this instant must survive the reconcile.
  const readStartedAt = Date.now()

  try {
    // Only OPEN alerts. The endpoint's 200-row cap covers open + acknowledged, but the store keeps
    // only open, so acknowledged rows would spend the budget and then be discarded client-side —
    // crowding out genuinely open alerts on a busy fleet.
    //
    // And no photo links (`include`): this runs every 5 s in every visible tab, and signed links
    // (~1.8 KB each, up to 50) would make each tick about ten times larger to cache what is almost
    // always cached already. The alert log asks for its own; any other photo (the emergency card's)
    // goes through the batched resolvers.
    const res = await robotsApi.listAlerts({ status: 'open' })
    const rows = res?.data?.alerts

    // A shape we do not recognise must not be treated as "no alerts" — reconciling an empty list
    // would silently clear the operator's inbox on a single bad response.
    if (!Array.isArray(rows)) return

    reconcileRobotAlerts(rows as RobotAlertItem[], readStartedAt)
  } catch {
    // Keep the last known list. A transient failure is not evidence that the alerts went away, and
    // clearing on error would make a flaky network look like an all-clear.
  } finally {
    inFlight = false
  }
}

/**
 * Mount the shared 5s reconcile while this component is alive.
 *
 * Safe to call from several components at once; only the first starts the interval and only the
 * last stops it.
 */
export function useRobotAlertsPolling(): void {
  useEffect(() => {
    if (!hasLiveChannel()) return undefined

    refCount += 1

    if (timer === null) {
      // Reconcile immediately on the first mount so a tab restored from sleep does not sit on a
      // stale list for the first interval.
      void pollOnce()
      timer = setInterval(() => void pollOnce(), ROBOT_ALERTS_POLL_MS)
    }

    return () => {
      refCount -= 1

      if (refCount <= 0 && timer !== null) {
        clearInterval(timer)
        timer = null
        refCount = 0
      }
    }
  }, [])
}
